/** Lux & billing: the Lux wallet (backend services/lux_wallet.py; docs/features/lux.md). The balance and what it
 * buys, this month's grant (reserved design Lux and free Lux, with its expiry), purchased Lux, the overdraft and
 * alerts; the plan and its usage (PlanUsage, with extra users); the upgrade section; packs at the plan's Lux price
 * with the first-purchase discount and codes; the rate card and metered usage; auto top-up and alerts; and the
 * activity log of every grant, purchase, charge and refund. Every amount arrives as an integer (hundredths of a Lux,
 * cents): this page formats them only. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, BatteryCharging, CreditCard, Gift, Info, Lock, Receipt, Sparkles, Tag } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { api, errorMessage, unwrap, type S } from "../api/client";
import { useMe, useOrg, useOrgId } from "../api/org";
import { useLuxActivity, useLuxWallet } from "../api/queries";
import { Alert, Badge, Button, Card, Empty, Field, PageHead, Select, Spinner, Switch, cx, useToast } from "../components/ui";
import { HelpLink } from "../components/HelpLink";
import { LuxCoin } from "../components/LuxIcon";
import { useLuxFormat, type LuxFormat } from "../components/LuxSpend";
import { useLeaving } from "../components/useOnce";
import { useLocale } from "../i18n/I18nProvider";
import { DirIcon } from "../i18n/mirror";
import type { MessageKey } from "../i18n/keys";
import { checkoutReturnUrls, safeReturnPath } from "../lib/luxSpend";
import { amountText, buysText, fmtLuxPrice, legsText, meterText, parseLux, perLuxMinor, rateUnit, share, worthMinor } from "../lib/lux";
import { FormattedMessage } from "react-intl";
import { useOrgPlan } from "./admin/OrgPlan";
import { ExpiryPointer, LuxDetails } from "./LuxDetails";
import { PlanUsage } from "./PlanUsage";
import { UpgradeSection } from "./Upgrade";

type Wallet = S["LuxWallet"];

/** Why Lux exists, in the owner's words (also on the landing page): `lux.why.text`. */
export const WHY_LUX_KEY: MessageKey = "lux.why.text";

/** The actions Lux pays for (backend domain/lux.py, assist/catalogue.py): their names and what they include, in the
 * reader's language; an action the catalog does not know keeps the server's English. */
const ACTION_TEXT: Record<string, { label: MessageKey; description?: MessageKey }> = {
  design: { label: "lux.action.design", description: "lux.action.designNote" },
  plan_set: { label: "lux.action.planSet", description: "lux.action.planSetNote" },
  widget_searches: { label: "lux.action.widgetSearches", description: "lux.action.searchesNote" },
  designer_searches: { label: "lux.action.designerSearches", description: "lux.action.searchesNote" },
  building_lookups: { label: "lux.action.buildingLookups", description: "lux.action.lookupsNote" },
  email_resends: { label: "lux.action.emailResends", description: "lux.action.resendsNote" },
  seat: { label: "lux.action.seat", description: "lux.action.seatNote" },
  "sketch.roof_hints": { label: "lux.action.roofHints", description: "lux.action.roofHintsNote" },
  "sketch.surroundings": { label: "lux.action.surroundings", description: "lux.action.surroundingsNote" },
  adjustment: { label: "lux.action.adjustment" },
};

function actionLabel(t: LuxFormat["t"], action: string | null | undefined, fallback: string | null | undefined): string {
  const known = action ? ACTION_TEXT[action] : undefined;
  return known ? t(known.label) : fallback || "";
}

function actionNote(t: LuxFormat["t"], action: string, fallback: string | null | undefined): string | null {
  const key = ACTION_TEXT[action]?.description;
  return key ? t(key) : fallback ?? null;
}

/** A wallet alert in the reader's language: English keeps the server's sentence (the most precise); another language
 * says the same from the catalog with the wallet's own numbers. */
function alertText(a: Wallet["alerts"][number], w: Wallet, f: LuxFormat, english: boolean): string {
  if (english) return a.message;
  const { t } = f;
  const granted = w.monthly.reserved_granted + w.monthly.free_granted;
  const left = w.monthly.reserved_left + w.monthly.free_left;
  switch (a.kind) {
    case "expiring":
      return t("lux.alert.expiring", { amount: f.lux(w.lots.expiring_soon), date: f.date(w.lots.next?.expires_at) });
    case "zero":
      return t("lux.alert.zero");
    case "low":
      return t("lux.alert.low", { amount: f.lux(w.balance), designs: Math.max(0, Math.floor(w.balance / 100)) });
    case "overdraft":
      return t("lux.alert.overdraft", { amount: f.lux(w.overdraft) });
    case "usage_100":
      return t("lux.alert.usage100");
    case "usage_80":
      return t("lux.alert.usage80", { used: f.pct(granted > 0 ? Math.floor(((granted - left) * 100) / granted) : 0) });
    case "top_up_failed":
      return t("lux.alert.topUpFailed");
    case "widget_cap":
      return t("lux.alert.widgetCap");
    case "seat_ending":
      return t("lux.alert.seatEnding");
    case "seats_over":
      return t("lux.alert.seatsOver");
    default:
      return a.message;
  }
}

function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** The hero: balance and what it buys, the month's grant split, purchased Lux, the advance, alerts. */
export function WalletCard({ w }: { w: Wallet }) {
  const f = useLuxFormat();
  const { t } = f;
  const english = useLocale().locale === "en";
  const m = w.monthly;
  const reserved = Math.max(m.reserved_left, 0) + Math.max(w.reserved_lots, 0); // design Lux: monthly + switch bonus
  const free = Math.max(m.free_left, 0);
  const purchased = Math.max(w.purchased, 0);
  const total = reserved + free + purchased;
  return (
    <Card className="lux-wallet">
      <div className="lux-hero">
        <div className="lux-balance">
          <span className="label">
            <LuxCoin size={16} /> {t("lux.wallet.balance")}
          </span>
          <div className="lux-amount">
            <span className="gradient-text i18n-ltr">{f.lux(w.balance)}</span> <small>Lux</small>
          </div>
          <div className="lux-buys">{buysText(w.buys, f)}</div>
          {w.unit_minor != null && <div className="muted small">{t("lux.wallet.unit", { price: f.money(w.unit_minor), plan: w.plan_name })}</div>}
        </div>
        <div className="lux-split">
          <div className="lux-bar" role="img" aria-label={t("lux.wallet.barLabel", { reserved: f.lux(reserved), free: f.lux(free), purchased: f.lux(purchased) })}>
            {total > 0 ? (
              <>
                <span className="is-reserved" style={{ width: `${share(reserved, total)}%` }} />
                <span className="is-free" style={{ width: `${share(free, total)}%` }} />
                <span className="is-purchased" style={{ width: `${Math.max(0, 100 - share(reserved, total) - share(free, total))}%` }} />
              </>
            ) : null}
          </div>
          <ul className="lux-legend">
            <li>
              <i className="is-reserved" />
              <div>
                <FormattedMessage id="lux.wallet.reserved" values={{ b: (c) => <b>{c}</b>, left: f.lux(m.reserved_left), granted: f.lux(m.reserved_granted) }} />
                <span>{t("lux.wallet.reservedNote", { bonus: w.reserved_lots > 0 ? f.lux(w.reserved_lots) : "none" })}</span>
              </div>
            </li>
            <li>
              <i className="is-free" />
              <div>
                <FormattedMessage id="lux.wallet.free" values={{ b: (c) => <b>{c}</b>, left: f.lux(m.free_left), granted: f.lux(m.free_granted) }} />
                <span>{t("lux.wallet.freeNote")}</span>
              </div>
            </li>
            <li>
              <i className="is-purchased" />
              <div>
                <FormattedMessage id="lux.wallet.purchased" values={{ b: (c) => <b>{c}</b>, amount: f.lux(w.purchased) }} />
                <span>
                  <ExpiryPointer summary={w.lots} />
                </span>
              </div>
            </li>
          </ul>
          <div className="lux-expiry muted small">
            {t("lux.wallet.expiry", { date: f.date(m.expires_at), next: f.lux(m.reserved_granted + m.free_granted) })}
          </div>
        </div>
      </div>
      {w.alerts.length > 0 && (
        <div className="lux-alerts">
          {w.alerts.map((a) => (
            <Alert key={a.kind + a.message} tone={a.tone === "danger" ? "danger" : a.tone === "warn" ? "warn" : "info"}>
              {alertText(a, w, f, english)}
            </Alert>
          ))}
        </div>
      )}
    </Card>
  );
}

function WhyLux() {
  const { t } = useLuxFormat();
  return (
    <div className="lux-why">
      <Info size={18} />
      <div>
        <b>{t("lux.why.title")}</b> {t(WHY_LUX_KEY)} <span className="muted">{t("lux.why.free")}</span>
      </div>
    </div>
  );
}

/** A long pack price (a currency written out, "14,441.50 US$" in Arabic) takes a smaller size so it fits its tile. */
function packPriceStyle(price: string): { fontSize?: number } {
  return price.replace(/[\u200e\u200f\u2066-\u2069]/g, "").length > 11 ? { fontSize: 16 } : {};
}

/** Packs at the plan's Lux price, the order summary (server-priced) and checkout (which comes back to `returnTo`,
 * the page that sent the user here with Buy Lux, when there is one). */
function Packs({ orgId, w, canBuy, returnTo }: { orgId: string; w: Wallet; canBuy: boolean; returnTo: string | null }) {
  const f = useLuxFormat();
  const { t } = f;
  const available = w.packs.filter((p) => p.available);
  const [pack, setPack] = useState<number>(available.find((p) => p.lux === 50)?.lux ?? available[0]?.lux ?? 10);
  const [codeText, setCodeText] = useState("");
  const code = useDebounced(codeText.trim(), 400);
  const quote = useQuery({
    queryKey: ["lux-price", orgId, pack, code],
    enabled: available.some((p) => p.lux === pack),
    queryFn: () =>
      unwrap(api.GET("/v1/orgs/{org_id}/lux/price", { params: { path: { org_id: orgId }, query: { pack: pack as 10, ...(code ? { code } : {}) } } })),
    placeholderData: (prev) => prev,
  });
  // one checkout however often it is clicked, and none again until the browser has left (components/useOnce.ts)
  const checkout = useLeaving({
    mutationFn: () =>
      unwrap(
        api.POST("/v1/orgs/{org_id}/lux/checkout", {
          params: { path: { org_id: orgId } },
          body: {
            pack: pack as 10,
            code: codeText.trim() || null,
            ...checkoutReturnUrls(window.location.origin, returnTo),
          },
        }),
      ),
    url: (c) => c.checkout_url,
    onError: () => void quote.refetch(),
  });
  const q = quote.data;
  const checking = code !== codeText.trim() || (quote.isFetching && quote.isPlaceholderData);
  return (
    <section className="lux-packs" id="packs">
      <div className="packs-head">
        <h2>{t("lux.packs.title")}</h2>
        <p>{t("lux.packs.intro")}</p>
      </div>
      <div className="split-wide">
        <Card title={t("lux.packs.card")} description={w.unit_minor != null ? t("lux.packs.priceAt", { plan: w.plan_name, price: f.money(w.unit_minor) }) : t("lux.packs.contract")}>
          <div className="stack-lg">
            {w.first_purchase.eligible && (
              <div className="offer-banner">
                <Gift />
                <div>
                  <b>{t("lux.packs.firstTitle", { off: f.pct(w.first_purchase.discount_pct) })}</b>
                  <div>
                    <span>{t("lux.packs.firstBody", { lux: f.num(w.first_purchase.max_lux) })}</span>
                  </div>
                </div>
              </div>
            )}
            <div className="tiers lux-pack-grid">
              {w.packs.map((p) => (
                <button
                  key={p.lux}
                  type="button"
                  className={cx("tier", pack === p.lux && "on", !p.available && "is-locked-pack")}
                  aria-pressed={pack === p.lux}
                  disabled={!p.available}
                  onClick={() => setPack(p.lux)}
                >
                  {p.lux === 200 && p.available && (
                    <span className="popular">
                      <Badge tone="grad" plain>{t("lux.packs.popular")}</Badge>
                    </span>
                  )}
                  <div className="tier-name">{t("lux.amount", { amount: f.num(p.lux) })}</div>
                  <div className="tier-price" style={packPriceStyle(f.moneyShort(p.total_minor))}>
                    {p.available ? f.moneyShort(p.total_minor) : <Lock size={16} aria-label={t("lux.packs.byContract")} />}
                  </div>
                  <div className="tier-range">
                    {p.available
                      ? t("lux.packs.perLux", { price: f.money(perLuxMinor(p)), off: p.discount_pct ? f.pct(p.discount_pct) : "none" })
                      : t("lux.packs.byContract")}
                  </div>
                </button>
              ))}
            </div>
            <p className="muted small" style={{ margin: 0 }}>
              {t("lux.packs.terms")}
            </p>
          </div>
        </Card>
        <Card title={t("lux.order.title")} className="sticky">
          {available.length === 0 ? (
            // Enterprise: Lux by contract, no pack to price (the query never runs: no endless spinner)
            <p className="muted" style={{ margin: 0 }}>{t("lux.packs.contract")}</p>
          ) : !q ? (
            quote.error ? <Alert tone="danger">{errorMessage(quote.error)}</Alert> : <Spinner />
          ) : (
            <div className="stack">
              <div>
                <div className="total-line">
                  <span className="muted">
                    {t("lux.order.line", { lux: f.num(q.lux), price: f.money(q.unit_minor) })}
                  </span>
                  <span>{f.money(q.subtotal_minor)}</span>
                </div>
                {q.pack_discount_minor > 0 && (
                  <div className="total-line" style={{ color: "var(--ok)" }}>
                    <span>
                      <Tag size={13} style={{ verticalAlign: -2 }} /> {t("lux.order.packOff", { off: f.pct(q.pack_discount_pct) })}
                    </span>
                    <span>−{f.money(q.pack_discount_minor)}</span>
                  </div>
                )}
                {q.first_purchase_minor > 0 && (
                  <div className="total-line" style={{ color: "var(--ok)" }}>
                    <span>
                      <Sparkles size={13} style={{ verticalAlign: -2 }} /> {t("lux.order.first", { off: f.pct(q.first_purchase_pct), lux: f.num(q.first_purchase_lux) })}
                    </span>
                    <span>−{f.money(q.first_purchase_minor)}</span>
                  </div>
                )}
                {q.coupon_minor > 0 && q.coupon_code && (
                  <div className="total-line" style={{ color: "var(--ok)" }}>
                    <span>
                      <Gift size={13} style={{ verticalAlign: -2 }} /> {t("lux.order.code", { code: q.coupon_code })}
                    </span>
                    <span>−{f.money(q.coupon_minor)}</span>
                  </div>
                )}
                {q.bonus_lux > 0 && (
                  <div className="total-line" style={{ color: "var(--ok)" }}>
                    <span>
                      <Gift size={13} style={{ verticalAlign: -2 }} /> {t("lux.order.codeBonus", { code: q.coupon_code ?? "" })}
                    </span>
                    <span>+{t("lux.amount", { amount: f.lux(q.bonus_lux) })}</span>
                  </div>
                )}
                <div className="total-line big">
                  <span>{t("lux.order.total")}</span>
                  <span>{f.money(q.total_minor)}</span>
                </div>
                <div className="hint right">{t("lux.order.average", { price: f.money(perLuxMinor({ lux: q.lux, total_minor: q.total_minor })) })}</div>
              </div>
              <input className="input" placeholder={t("lux.order.promo")} aria-label={t("lux.order.promo")} maxLength={40} value={codeText} onChange={(e) => setCodeText(e.target.value)} dir="auto" />
              {checking ? (
                <span className="small faint">{t("lux.order.checking")}</span>
              ) : (
                q.coupon_error && codeText.trim() && <span className="small" style={{ color: "var(--danger)" }}>{q.coupon_error}</span>
              )}
              {checkout.error != null && <Alert tone="danger">{errorMessage(checkout.error)}</Alert>}
              <Button variant="primary" size="lg" block icon={<CreditCard />} disabled={!canBuy || checking || !w.checkout_available} loading={checkout.busy} onClick={() => checkout.run(undefined)}>
                {t("lux.order.continue")}
              </Button>
              <p className="hint">{canBuy ? t("lux.order.secure") : t("lux.order.noRole")}</p>
            </div>
          )}
        </Card>
      </div>
    </section>
  );
}

/** The rate card at the plan's price, and what the plan includes before Lux is spent. */
function RateCard({ w }: { w: Wallet }) {
  const f = useLuxFormat();
  const { t } = f;
  return (
    <Card title={t("lux.rates.title")} description={t("lux.rates.description")}>
      <div className="table-wrap">
        <table className="table lux-rates">
          <thead>
            <tr>
              <th>{t("lux.rates.action")}</th>
              <th className="num">Lux</th>
              <th className="num hide-sm">{t("lux.rates.worth")}</th>
            </tr>
          </thead>
          <tbody>
            {w.rate_card.map((r) => {
              const worth = worthMinor(r.price, w.unit_minor);
              return (
                <tr key={r.action}>
                  <td>
                    <b>{actionLabel(t, r.action, r.label)}</b>
                    <div className="muted small">{actionNote(t, r.action, r.description)}</div>
                  </td>
                  <td className="num nowrap">
                    <b>{t("lux.amount", { amount: fmtLuxPrice(r.price, f.locale) })}</b>
                    <div className="muted small">{rateUnit(r, f)}</div>
                  </td>
                  <td className="num hide-sm faint">{worth != null ? f.money(worth) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="lux-meters">
        {w.meters.map((m) => (
          <div key={m.action} className="lux-meter">
            <div className="lux-meter-head">
              <span>{actionLabel(t, m.action, m.label)}</span>
              <span className="muted small">{meterText(m, f)}</span>
            </div>
            <div className={cx("usage-bar", m.included != null && m.used > m.included && "is-over")}>
              <span style={{ width: `${m.included ? share(Math.min(m.used, m.included), m.included) : m.used ? 100 : 0}%` }} />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/** Auto top-up (off by default), the low-balance alert and the website searches' daily ceiling (off by default). */
function decimalSign(locale: string): string {
  return new Intl.NumberFormat(locale).formatToParts(1.5).find((p) => p.type === "decimal")?.value ?? ".";
}

/** A Lux amount for an input, in the reader's way without grouping ("1250,00" in German). */
function editLux(hundredths: number, locale: string): string {
  const abs = Math.abs(Math.trunc(hundredths));
  const whole = Math.floor(abs / 100);
  return `${hundredths < 0 ? "-" : ""}${whole}${decimalSign(locale)}${String(abs - whole * 100).padStart(2, "0")}`;
}

/** A Lux amount typed in the reader's way ("12,50" in German, "1.250,00" too) into hundredths. */
function parseLocalLux(text: string, locale: string): number | undefined {
  const decimal = decimalSign(locale);
  const plain = text.replace(/[\s\u00a0\u202f']/g, "");
  if (decimal === ".") return parseLux(plain);
  // the decimal sign is the last "," (or the locale's own); the other separators group thousands
  const at = plain.lastIndexOf(decimal);
  const normal = at < 0 ? plain.replace(/[.,]/g, "") : `${plain.slice(0, at).replace(/[.,]/g, "")}.${plain.slice(at + 1)}`;
  return parseLux(normal);
}

/** The settings' limits (the contract's LuxSettingsWrite): the balance thresholds in hundredths, the month in Lux. */
const MAX_BELOW = 100_000;
const MAX_MONTH = 100_000;
const MAX_LOW = 10_000_000;

function Settings({ orgId, w, canBuy }: { orgId: string; w: Wallet; canBuy: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const f = useLuxFormat();
  const { t } = f;
  const edit = (h: number) => editLux(h, f.locale);
  const a = w.auto_top_up;
  const [enabled, setEnabled] = useState(a.enabled);
  const [pack, setPack] = useState(String(a.pack));
  const [below, setBelow] = useState(edit(a.below));
  const [max, setMax] = useState(String(a.month_max));
  const [low, setLow] = useState(edit(w.low_alert_below));
  const [capOn, setCapOn] = useState(w.widget.custom != null);
  const [cap, setCap] = useState(w.widget.custom != null ? edit(w.widget.custom) : "");
  // every field checked before saving: an empty, negative, unreadable or too large amount is said at its field and
  // nothing is sent (it used to save the old value silently and say "Saved")
  const belowLux = parseLocalLux(below, f.locale);
  const maxLux = /^\d{1,6}$/.test(max.trim()) ? Number(max.trim()) : undefined;
  const lowLux = parseLocalLux(low, f.locale);
  const capLux = capOn ? parseLocalLux(cap, f.locale) : null;
  const range = (v: number | undefined, top: number) => (v == null || v > top ? t("lux.settings.amountError", { max: f.lux(top) }) : null);
  const errors = {
    below: range(belowLux, MAX_BELOW),
    max:
      maxLux == null || maxLux > MAX_MONTH
        ? t("lux.settings.amountError", { max: f.num(MAX_MONTH) })
        : enabled && maxLux < Number(pack)
          ? t("lux.settings.maxPackError", { lux: f.num(Number(pack)) })
          : null,
    low: range(lowLux, MAX_LOW),
    cap: capOn && (capLux == null || capLux > MAX_LOW) ? t("lux.settings.capError") : null,
  };
  const invalid = Object.values(errors).some(Boolean);
  const [tried, setTried] = useState(false);
  const save = useMutation({
    mutationFn: () => {
      const body: S["LuxSettingsWrite"] = {
        auto_top_up: enabled,
        auto_top_up_pack: Number(pack) as 10,
        auto_top_up_below: belowLux!,
        auto_top_up_month_max: maxLux!,
        low_alert_below: lowLux!,
        widget_daily_cap: capLux ?? null,
      };
      return unwrap(api.PUT("/v1/orgs/{org_id}/lux/settings", { params: { path: { org_id: orgId } }, body }));
    },
    onSuccess: (data) => {
      qc.setQueryData(["lux", orgId], data);
      toast(t("lux.settings.saved"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const packs = w.packs.filter((p) => p.available);
  return (
    <Card className="lux-settings" title={<span className="row" style={{ gap: 8 }}><BatteryCharging size={18} /> {t("lux.settings.title")}</span>} description={t("lux.settings.description")}>
      <div className="stack" id="lux-settings">
        <SpendPreference />
        <b className="lux-settings-sub">{t("lux.settings.autoTopUp")}</b>
        <Switch checked={enabled} onChange={setEnabled} disabled={!canBuy} label={t("lux.settings.autoTopUpSwitch")} />
        {!a.card_on_file && <div className="muted small">{t("lux.settings.cardFirst")}</div>}
        <div className="form-grid">
          <Field label={t("lux.settings.pack")}>
            <Select value={pack} onChange={setPack} ariaLabel={t("lux.settings.packAria")} options={packs.map((p) => ({ value: String(p.lux), label: t("lux.settings.packOption", { lux: f.num(p.lux), price: f.moneyShort(p.total_minor) }) }))} />
          </Field>
          <Field label={t("lux.settings.below")} error={tried ? errors.below : null}>
            <input className="input" inputMode="decimal" dir="ltr" value={below} onChange={(e) => setBelow(e.target.value)} />
          </Field>
          <Field label={t("lux.settings.max")} hint={t("lux.settings.maxHint", { lux: f.num(a.bought_this_month) })} error={tried ? errors.max : null}>
            <input className="input" inputMode="numeric" dir="ltr" value={max} onChange={(e) => setMax(e.target.value)} />
          </Field>
          <Field label={t("lux.settings.low")} hint={t("lux.settings.lowHint", { warn: f.pct(80), full: f.pct(100) })} error={tried ? errors.low : null}>
            <input className="input" inputMode="decimal" dir="ltr" value={low} onChange={(e) => setLow(e.target.value)} />
          </Field>
        </div>
        <Switch checked={capOn} onChange={setCapOn} disabled={!canBuy} label={t("lux.settings.capSwitch")} />
        {capOn ? (
          <div className="form-grid">
            <Field label={t("lux.settings.cap")} hint={t("lux.settings.capHint", { spent: f.lux(w.widget.spent_today) })} error={tried ? errors.cap : null}>
              <input
                className="input"
                inputMode="decimal"
                dir="ltr"
                placeholder={w.widget.default != null ? edit(w.widget.default) : t("lux.settings.capPlaceholder", { example: f.lux(100) })}
                value={cap}
                onChange={(e) => setCap(e.target.value)}
              />
            </Field>
          </div>
        ) : (
          <div className="muted small">
            {w.widget.default != null
              ? t("lux.settings.capOffPlan", { cap: f.lux(w.widget.default), spent: f.lux(w.widget.spent_today) })
              : t("lux.settings.capOffDefault", { spent: f.lux(w.widget.spent_today) })}
          </div>
        )}
        <div>
          <Button
            variant="primary"
            disabled={!canBuy || (tried && invalid)}
            loading={save.isPending}
            onClick={() => {
              setTried(true);
              if (!invalid && !save.isPending) save.mutate();
            }}
          >
            {t("lux.settings.save")}
          </Button>
        </div>
      </div>
    </Card>
  );
}

/** The signed-in user's own choice: ask before a click spends Lux (components/LuxSpend.tsx). Any member. */
function SpendPreference() {
  const { t } = useLuxFormat();
  const me = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const on = me.data?.preferences?.confirm_lux_spend !== false;
  const save = useMutation({
    mutationFn: (value: boolean) => unwrap(api.PATCH("/v1/me", { body: { confirm_lux_spend: value } })),
    onSuccess: (data) => {
      qc.setQueryData(["me"], data);
      toast(data.preferences?.confirm_lux_spend === false ? t("lux.pref.off") : t("lux.pref.on"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  return (
    <div className="lux-pref">
      <Switch checked={on} disabled={!me.data || save.isPending} onChange={(v) => save.mutate(v)} label={t("lux.pref.label")} />
      <div className="muted small">{t("lux.pref.help")}</div>
    </div>
  );
}

const ACTIONS: { value: string; label: MessageKey }[] = [
  { value: "", label: "lux.activity.filter.all" },
  { value: "design", label: "lux.activity.filter.designs" },
  { value: "plan_set", label: "lux.activity.filter.planSets" },
  { value: "widget_searches", label: "lux.activity.filter.widgetSearches" },
  { value: "designer_searches", label: "lux.activity.filter.designerSearches" },
  { value: "building_lookups", label: "lux.activity.filter.buildingLookups" },
  { value: "email_resends", label: "lux.activity.filter.resends" },
  { value: "seat", label: "lux.activity.filter.seats" },
  { value: "sketch.roof_hints", label: "lux.activity.filter.roofHints" },
  { value: "sketch.surroundings", label: "lux.activity.filter.surroundings" },
];

const KIND: Record<string, { label: MessageKey; tone?: "ok" | "warn" | "danger" | "info" | "grad" }> = {
  monthly: { label: "lux.activity.kind.monthly", tone: "grad" },
  purchase: { label: "lux.activity.kind.purchase", tone: "ok" },
  grant: { label: "lux.activity.kind.grant", tone: "info" },
  charge: { label: "lux.activity.kind.charge" },
  refund: { label: "lux.activity.kind.refund", tone: "ok" },
  settle: { label: "lux.activity.kind.settle", tone: "info" },
  expire: { label: "lux.activity.kind.expire", tone: "danger" },
};

/** Every grant, purchase, charge, refund and expiry, with the pools that paid. */
/** The usage log. `compact` stacks each row (date under the description) for a narrow column. */
export function Activity({ orgId, showIds = false, compact = false }: { orgId: string; showIds?: boolean; compact?: boolean }) {
  const [params] = useSearchParams();
  const linked = params.get("activity") ?? "";
  const [action, setAction] = useState(linked);
  // a link to the log filtered (a Lux toast's "Usage log") while already on this page: the filter follows it
  useEffect(() => setAction(linked), [linked]);
  const activity = useLuxActivity(orgId, action);
  const f = useLuxFormat();
  const { t } = f;
  const items = activity.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Card
      className="lux-activity-card"
      title={<span className="row" style={{ gap: 8 }} id="lux-activity"><Receipt size={18} /> {t("lux.activity.title")}</span>}
      pad={false}
      actions={<Select value={action} onChange={setAction} ariaLabel={t("lux.activity.show")} options={ACTIONS.map((o) => ({ value: o.value, label: t(o.label) }))} />}
    >
      {activity.isLoading ? (
        <Spinner />
      ) : items.length === 0 ? (
        <Empty icon={<Receipt />} title={t("lux.activity.empty")} />
      ) : (
        <div className="table-wrap">
          <table className={cx("table lux-activity", compact && "is-compact")}>
            <thead>
              <tr>
                <th>{t("lux.activity.date")}</th>
                <th>{t("lux.activity.what")}</th>
                <th className="hide-sm">{t("lux.activity.from")}</th>
                <th className="num">Lux</th>
              </tr>
            </thead>
            <tbody>
              {items.map((op) => (
                <tr key={op.id}>
                  <td className="nowrap lux-when">{f.date(op.created_at, true)}</td>
                  <td className="lux-op">
                    <Badge tone={KIND[op.kind]?.tone}>{KIND[op.kind] ? t(KIND[op.kind].label) : op.kind}</Badge>{" "}
                    <span className="lux-what" dir="auto">{op.note || actionLabel(t, op.action, op.action_label)}</span>
                    {op.refunded > 0 && <span className="muted small"> · {t("lux.activity.refunded", { amount: f.lux(op.refunded) })}</span>}
                    {showIds && op.kind === "charge" && <div className="mono faint small">{op.id}</div>}
                  </td>
                  <td className="hide-sm muted small lux-from">{legsText(op.legs, f)}</td>
                  <td className={cx("num nowrap lux-op-amount", op.amount > 0 && "is-plus")} style={{ fontWeight: 600 }} dir="ltr">
                    {amountText(op.amount, f.locale)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {activity.hasNextPage && (
            <div style={{ padding: 12, textAlign: "center" }}>
              <Button size="sm" loading={activity.isFetchingNextPage} onClick={() => activity.fetchNextPage()}>
                {t("lux.activity.more")}
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export function Lux() {
  const orgId = useOrgId();
  const { can } = useOrg();
  const canBuy = can("credits_billing", "create");
  const [params, setParams] = useSearchParams();
  const wallet = useLuxWallet(orgId);
  const plan = useOrgPlan(orgId, false);
  const location = useLocation();
  const { t } = useLuxFormat();
  const raw = params.get("checkout");
  // Buy Lux from a page (the Permit workspace): checkout goes straight back there
  const returnTo = safeReturnPath(params.get("return"));
  const status = raw === "paid" ? "success" : raw === "cancel" ? "cancelled" : raw;
  // back from paying: the Lux arrives with the payment's webhook (usually seconds), so the wallet, its lots and the
  // usage log are read again for half a minute, not once (once was often too early: the old balance stayed)
  const qc = useQueryClient();
  useEffect(() => {
    if (status !== "success") return;
    const read = () => {
      void qc.invalidateQueries({ queryKey: ["lux", orgId] });
      void qc.invalidateQueries({ queryKey: ["lux-activity", orgId] });
    };
    read();
    let n = 0;
    const id = window.setInterval(() => (++n >= 12 ? window.clearInterval(id) : read()), 2500);
    return () => window.clearInterval(id);
  }, [status, orgId, qc]);
  const loaded = !!wallet.data;
  useEffect(() => {
    // links from the Lux toasts and the confirmation: the usage log, the settings
    if (!loaded || !location.hash) return;
    // twice: the sections above it (plan, usage, packs) arrive with their own queries and push it down
    const go = () => document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: "start" });
    const ids = [window.setTimeout(go, 150), window.setTimeout(go, 1200)];
    return () => ids.forEach((id) => window.clearTimeout(id));
  }, [loaded, location.hash]);
  if (wallet.isLoading) return <Spinner />;
  if (wallet.error || !wallet.data) return <Alert tone="danger">{errorMessage(wallet.error)}</Alert>;
  const w = wallet.data;
  const upgrade = plan.data ? <UpgradeSection orgId={orgId} plan={plan.data} canBuy={canBuy} /> : null;
  return (
    <>
      <PageHead
        title={<span className="lux-page-title"><LuxCoin size={30} /> {t("lux.page.title")}</span>}
        description={t("lux.page.description")}
        actions={<HelpLink article="lux-and-billing/" label={t("lux.page.help")} />}
      />
      {status === "success" && (
        <div style={{ marginBottom: 16 }}>
          <Alert tone="ok" title={t("lux.checkout.paidTitle")} action={<Button size="sm" variant="ghost" onClick={() => setParams({})}>{t("lux.dismiss")}</Button>}>
            {t("lux.checkout.paidBody")}
          </Alert>
        </div>
      )}
      {status === "cancelled" && (
        <div style={{ marginBottom: 16 }}>
          <Alert tone="warn" title={t("lux.checkout.cancelledTitle")} action={<Button size="sm" variant="ghost" onClick={() => setParams({})}>{t("lux.dismiss")}</Button>}>
            {t("lux.checkout.cancelledBody")}
          </Alert>
        </div>
      )}
      {returnTo && (
        <div style={{ marginBottom: 16 }}>
          <Alert
            tone="info"
            title={t("lux.return.title")}
            action={
              <Link className="btn btn-ghost btn-sm" to={returnTo}>
                <DirIcon>
                  <ArrowLeft size={14} />
                </DirIcon>{" "}
                {t("lux.return.back")}
              </Link>
            }
          >
            {t("lux.return.body", { place: returnTo.includes("/permit") ? "permit" : "other" })}
          </Alert>
        </div>
      )}
      <div className="stack-lg">
        <WalletCard w={w} />
        <WhyLux />
        <div id="plans" />
        {plan.data?.plan === "free" && upgrade}
        <PlanUsage orgId={orgId} />
        {plan.data?.plan !== "free" && upgrade}
        <Packs orgId={orgId} w={w} canBuy={canBuy} returnTo={returnTo} />
        <div className="grid-2 lux-two">
          <RateCard w={w} />
          <Settings orgId={orgId} w={w} canBuy={canBuy} />
        </div>
        <LuxDetails orgId={orgId} />
        <Activity orgId={orgId} />
      </div>
    </>
  );
}

